<template>
  <v-app>
    <v-app-bar color="primary" dark elevation="2">
      <!-- Hamburger menu (mobile only) -->
      <v-app-bar-nav-icon 
        class="d-md-none" 
        @click="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <v-toolbar-title class="cursor-pointer" @click="$router.push('/')">
        <v-icon class="mr-2">mdi-account-group</v-icon>
        <span class="d-none d-sm-inline">{{ $t('home.title') }}</span>
      </v-toolbar-title>
      
      <v-spacer></v-spacer>

      <!-- Desktop Navigation Buttons (hidden on mobile) -->
      <v-btn 
        v-for="item in navItems" 
        :key="item.path"
        :to="item.path"
        text
        class="mr-2 d-none d-md-flex"
      >
        {{ item.label }}
        <v-badge
          v-if="item.badge && item.badge > 0"
          :content="item.badge"
          color="error"
          floating
          class="ml-1"
        ></v-badge>
      </v-btn>

      <!-- Language Switcher -->
      <v-menu>
        <template v-slot:activator="{ props: menuProps }">
          <v-btn icon v-bind="menuProps">
            <v-icon>mdi-translate</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item 
            v-for="lang in availableLocales" 
            :key="lang.code"
            @click="changeLanguage(lang.code)"
            :class="{ 'v-list-item--active': locale === lang.code }"
          >
            <v-list-item-title>
              <span class="mr-2">{{ lang.flag }}</span>
              {{ lang.name }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- User Menu (if authenticated) -->
      <v-menu v-if="isAuthenticated">
        <template v-slot:activator="{ props: menuProps }">
          <v-btn icon v-bind="menuProps">
            <v-avatar color="secondary" size="32">
              <v-img v-if="user?.avatarUrl" :src="user.avatarUrl"></v-img>
              <span v-else>{{ userName }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="goToMyProfile">
            <template v-slot:prepend>
              <v-icon>mdi-account</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.profile') }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isAdmin" @click="$router.push('/admin')">
            <template v-slot:prepend>
              <v-icon>mdi-shield-crown</v-icon>
            </template>
            <v-list-item-title>{{ $t('admin.title') }}</v-list-item-title>
          </v-list-item>
          <v-divider />
          <v-list-item @click="logout">
            <template v-slot:prepend>
              <v-icon>mdi-logout</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      
      <!-- Accessibility Settings -->
      <accessibility-settings v-if="isAuthenticated" />
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer v-model="drawer" temporary>
      <!-- User info header -->
      <div v-if="isAuthenticated" class="drawer-header pa-4">
        <div class="d-flex align-center">
          <v-avatar color="primary" size="48">
            <v-img v-if="user?.avatarUrl" :src="user.avatarUrl"></v-img>
            <span v-else class="text-white text-h6">{{ userName }}</span>
          </v-avatar>
          <div class="ml-3">
            <div class="text-subtitle-1 font-weight-medium">{{ user?.displayName || user?.username }}</div>
            <div class="text-caption text-grey">@{{ user?.username }}</div>
          </div>
        </div>
      </div>
      <v-divider v-if="isAuthenticated"></v-divider>

      <v-list density="compact" nav>
        <v-list-item
          v-for="item in drawerItems"
          :key="item.path"
          :to="item.path"
          :prepend-icon="item.icon"
          @click="drawer = false"
        >
          <v-list-item-title>
            {{ item.label }}
          </v-list-item-title>
          <template v-slot:append>
            <v-badge
              v-if="item.badge && item.badge > 0"
              :content="item.badge"
              color="error"
              inline
            ></v-badge>
          </template>
        </v-list-item>
      </v-list>

      <v-divider v-if="isAuthenticated"></v-divider>

      <v-list v-if="isAuthenticated" density="compact" nav>
        <v-list-item prepend-icon="mdi-account" @click="goToMyProfile(); drawer = false">
          <v-list-item-title>{{ $t('nav.profile') }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isAdmin" prepend-icon="mdi-shield-crown" @click="$router.push('/admin'); drawer = false">
          <v-list-item-title>{{ $t('admin.title') }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-2">
          <v-btn
            v-if="isAuthenticated"
            block
            variant="tonal"
            color="error"
            prepend-icon="mdi-logout"
            @click="logout(); drawer = false"
          >
            {{ $t('nav.logout') }}
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <v-main>
      <router-view />
    </v-main>
    <site-footer /> 
    <!-- Floating Chat Widget -->
    <chat-widget v-if="isAuthenticated" ref="chatWidgetRef" />
    <!-- Reader Mode (floating button + fullscreen popup) -->
    <reader-mode v-if="isAuthenticated" />
  </v-app>
</template>

<script setup>
/* eslint-disable */
import { computed, ref, onMounted, watch, provide, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import { useDisplay } from 'vuetify'
import { availableLocales } from './plugins/i18n'
import api from './services/api'
import { heartbeatService } from './services/heartbeat'
import ChatWidget from './components/ChatWidget.vue'
import AccessibilitySettings from './components/AccessibilitySettings.vue'
import { initAccessibility, accessibilitySettings } from './services/accessibility'
import SiteFooter from './components/SiteFooter.vue'
import ReaderMode from './components/ReaderMode.vue'

const chatWidgetRef = ref(null)
const vuetifyTheme = useTheme()
const { mdAndUp } = useDisplay()
const drawer = ref(false)

// Initialize accessibility settings on app load
initAccessibility()

// Apply saved theme on startup
if (accessibilitySettings.theme) {
  vuetifyTheme.global.name.value = accessibilitySettings.theme
}

const store = useStore()
const router = useRouter()
const { locale, t } = useI18n()

const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])
const user = computed(() => store.getters['auth/user'])
const userName = computed(() => user.value?.displayName?.charAt(0) || 'U')
const isAdmin = ref(false)

const pendingFriendRequests = ref(0)
const pendingGroupInvites = ref(0)

// Check admin status
const checkAdminStatus = async () => {
  if (!isAuthenticated.value) {
    isAdmin.value = false
    return
  }
  try {
    const response = await api.get('/admin/users', { params: { limit: 1 } })
    isAdmin.value = true
    if (user.value && !user.value.isAdmin) {
      store.commit('auth/SET_USER', { ...user.value, isAdmin: true })
    }
  } catch {
    isAdmin.value = false
  }
}

const fetchPendingFriendRequests = async () => {
  if (!isAuthenticated.value) {
    pendingFriendRequests.value = 0
    return
  }
  
  try {
    const response = await api.get('/friends', { params: { status: 'pending' } })
    pendingFriendRequests.value = response.data.count || 0
  } catch (error) {
    console.error('Error fetching friend requests:', error)
    pendingFriendRequests.value = 0
  }
}

const fetchPendingGroupInvites = async () => {
  if (!isAuthenticated.value) {
    pendingGroupInvites.value = 0
    return
  }
  
  try {
    const response = await api.get('/invites')
    pendingGroupInvites.value = response.data.count || 0
  } catch (error) {
    console.error('Error fetching group invites:', error)
    pendingGroupInvites.value = 0
  }
}

// Provide refresh function to child components
provide('refreshFriendRequests', fetchPendingFriendRequests)
provide('refreshGroupInvites', fetchPendingGroupInvites)
const blogUnread = computed(() => store.getters['blog/totalUnread'])

// Desktop nav items (shown in app bar)
const navItems = computed(() => {
  const items = [
    { path: '/', label: t('nav.home') },
  ]
  
  if (isAuthenticated.value) {
    items.push(
      { path: '/wall', label: t('nav.wall') },
      { path: '/groups', label: t('nav.groups'), badge: pendingGroupInvites.value },
      { path: '/blog', label: t('nav.blog'), badge: blogUnread.value },
      { path: '/friends', label: t('nav.friends'), badge: pendingFriendRequests.value },
      { path: '/users', label: t('nav.users') }
    )
  } else {
    items.push(
      { path: '/blog', label: t('nav.blog') },
      { path: '/login', label: t('nav.login') }
    )
  }
  
  return items
})

// Drawer nav items (with icons for mobile)
const drawerItems = computed(() => {
  const items = [
    { path: '/', label: t('nav.home'), icon: 'mdi-home' },
  ]
  
  if (isAuthenticated.value) {
    items.push(
      { path: '/wall', label: t('nav.wall'), icon: 'mdi-post' },
      { path: '/groups', label: t('nav.groups'), icon: 'mdi-account-group', badge: pendingGroupInvites.value },
      { path: '/friends', label: t('nav.friends'), icon: 'mdi-account-heart', badge: pendingFriendRequests.value },
      { path: '/users', label: t('nav.users'), icon: 'mdi-account-search' },
      { path: '/faq', label: t('footer.faq'), icon: 'mdi-frequently-asked-questions' }
    )
  } else {
    items.push(
      { path: '/login', label: t('nav.login'), icon: 'mdi-login' }
    )
  }
  
  return items
})

// Provide function to start chat from anywhere (e.g., profile page)
const startChatWithUser = (userId, userData) => {
  if (chatWidgetRef.value) {
    chatWidgetRef.value.startChatWithUser(userId, userData)
  }
}
provide('startChatWithUser', startChatWithUser)

const changeLanguage = (lang) => {
  locale.value = lang
  localStorage.setItem('language', lang)
}

const logout = () => {
  store.dispatch('auth/logout')
  router.push('/')
}

const goToMyProfile = () => {
  router.push(`/profile/${user.value?.username}`)
}

// Close drawer when switching to desktop
watch(mdAndUp, (isDesktop) => {
  if (isDesktop) {
    drawer.value = false
  }
})

// Fetch pending requests on mount and when auth changes
onMounted(() => {
  fetchPendingFriendRequests()
  fetchPendingGroupInvites()
  checkAdminStatus()
})

watch(isAuthenticated, (newVal) => {
  if (newVal) {
    fetchPendingFriendRequests()
    fetchPendingGroupInvites()
    checkAdminStatus()
    heartbeatService.start()
  } else {
    pendingFriendRequests.value = 0
    pendingGroupInvites.value = 0
    isAdmin.value = false
    heartbeatService.stop()
  }
})

// Refresh pending requests periodically (every 60 seconds)
let refreshInterval = null
onMounted(() => {
  if (isAuthenticated.value) {
    heartbeatService.start()
  }
  
  refreshInterval = setInterval(() => {
    if (isAuthenticated.value) {
      fetchPendingFriendRequests()
      fetchPendingGroupInvites()
    }
  }, 60000)
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
  heartbeatService.stop()
})
</script>

<style>
#app {
  font-family: 'Roboto', sans-serif;
}

.cursor-pointer {
  cursor: pointer;
}

.drawer-header {
  background: rgba(var(--v-theme-primary), 0.08);
}
</style>